/**
 * Authentication hook for managing user authentication state
 */

import { useState, useEffect, useCallback, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { 
  getCurrentSession, 
  validateSession, 
  clearAuthToken,
  setupTokenExpirationCheck 
} from '../utils/session.js';
import { handleOAuthCallback, revokeGoogleToken, hasPermission, hasRole } from '../utils/auth.js';

/**
 * Custom hook for authentication management
 */
export const useAuth = () => {
  const authContext = useContext(AuthContext);
  
  if (!authContext) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return authContext;
};

/**
 * Hook for handling OAuth callback
 */
export const useOAuthCallback = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { login } = useAuth();

  const handleCallback = useCallback(async (code, state) => {
    setLoading(true);
    setError(null);

    try {
      const authData = await handleOAuthCallback(code, state);
      await login(authData);
      
      // Redirect to dashboard on successful authentication
      window.location.href = '/';
      
    } catch (err) {
      console.error('OAuth callback error:', err);
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  }, [login]);

  return { handleCallback, loading, error };
};

/**
 * Hook for managing authentication session lifecycle
 */
export const useAuthSession = () => {
  const [sessionValid, setSessionValid] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const { logout } = useAuth();

  const checkSession = useCallback(() => {
    const session = validateSession();
    setSessionValid(!!session);
    return !!session;
  }, []);

  const handleSessionExpired = useCallback(() => {
    logout();
    // Optionally show notification to user about session expiration
    console.log('Session expired - user logged out');
  }, [logout]);

  useEffect(() => {
    // Initial session check
    const isValid = checkSession();
    setCheckingSession(false);

    if (isValid) {
      // Set up automatic session expiration monitoring
      const cleanupInterval = setupTokenExpirationCheck(handleSessionExpired);
      
      return () => {
        if (cleanupInterval) {
          clearInterval(cleanupInterval);
        }
      };
    }
  }, [checkSession, handleSessionExpired]);

  return {
    sessionValid,
    checkingSession,
    checkSession
  };
};

/**
 * Hook for permission-based access control
 */
export const usePermissions = () => {
  const { user, role, permissions } = useAuth();

  const checkPermission = useCallback((requiredPermission) => {
    return hasPermission(permissions, requiredPermission);
  }, [permissions]);

  const checkRole = useCallback((requiredRole) => {
    return hasRole(role, requiredRole);
  }, [role]);

  const canAccess = useCallback((requiredPermission = null, requiredRole = null) => {
    if (requiredPermission && !checkPermission(requiredPermission)) {
      return false;
    }

    if (requiredRole && !checkRole(requiredRole)) {
      return false;
    }

    return true;
  }, [checkPermission, checkRole]);

  return {
    permissions,
    role,
    checkPermission,
    checkRole,
    canAccess,
    isAdmin: role === 'admin',
    isTalentPartner: role === 'talent-partner' || role === 'admin'
  };
};

/**
 * Hook for logout functionality
 */
export const useLogout = () => {
  const [loggingOut, setLoggingOut] = useState(false);
  const { logout: contextLogout, user } = useAuth();

  const performLogout = useCallback(async () => {
    setLoggingOut(true);

    try {
      // Try to revoke Google token if available
      const session = getCurrentSession();
      if (session?.tokens?.access_token) {
        await revokeGoogleToken(session.tokens.access_token);
      }
    } catch (error) {
      console.error('Error revoking Google token:', error);
      // Continue with logout even if token revocation fails
    }

    try {
      // Clear local session
      clearAuthToken();
      contextLogout();
      
      // Redirect to login page
      window.location.href = '/login';
      
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setLoggingOut(false);
    }
  }, [contextLogout, user]);

  return { performLogout, loggingOut };
};

/**
 * Hook for checking authentication status
 */
export const useAuthStatus = () => {
  const { isAuthenticated, user, role } = useAuth();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Initialize authentication status check
    const checkAuthStatus = async () => {
      try {
        const session = validateSession();
        setLoading(false);
      } catch (error) {
        console.error('Error checking auth status:', error);
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  return {
    isAuthenticated,
    user,
    role,
    loading
  };
};

/**
 * Hook for handling authentication errors
 */
export const useAuthError = () => {
  const [authError, setAuthError] = useState(null);

  const clearError = useCallback(() => {
    setAuthError(null);
  }, []);

  const handleError = useCallback((error) => {
    const errorMessage = error?.message || 'An authentication error occurred';
    setAuthError(errorMessage);
  }, []);

  return {
    authError,
    clearError,
    handleError
  };
};